import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Hide navbar on tribute workspace pages (but not SEO utility pages like /tribute-video)
  const isTributeWorkspace = /^\/tribute\/[^/]+/.test(location.pathname);
  if (isTributeWorkspace) {
    return null;
  }

  const isHomepage = location.pathname === "/";

  const navLinks = [
    { label: "Why Living Tribute", href: "/why-living-tribute" },
    { label: "Plan Ahead", href: "/plan-ahead" },
    { label: "Pricing", href: "/pricing" },
  ];

  const resourceLinks = [
    { label: "Getting Started", href: "/resources#getting-started" },
    { label: "Pet Tributes", href: "/pets" },
    { label: "Gift a Tribute", href: "/gift" },
    { label: "Support Center", href: "/support" },
  ];

  // Determine styles based on scroll state and page
  const isTransparent = isHomepage && !isScrolled && !mobileMenuOpen;
  
  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent 
          ? "bg-transparent" 
          : "bg-background/95 backdrop-blur-md border-b border-border/50 shadow-sm"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <Heart className={`w-7 h-7 transition-colors duration-300 ${
              isTransparent ? "text-white fill-white/80" : "text-accent fill-accent"
            }`} />
            <span className={`text-xl font-semibold transition-colors duration-300 ${
              isTransparent ? "text-white" : "text-foreground"
            }`}>
              Living Tribute
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  isTransparent 
                    ? "text-white/80 hover:text-white" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                isTransparent 
                  ? "text-white/80 hover:text-white" 
                  : "text-muted-foreground hover:text-foreground"
              }`}>
                Resources & Support
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {resourceLinks.map((link) => (
                  <DropdownMenuItem key={link.href} asChild>
                    <Link to={link.href}>{link.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <Button 
                asChild 
                variant="ghost" 
                className={`text-sm font-medium ${
                  isTransparent ? "text-white hover:bg-white/10" : ""
                }`}
              >
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <Link
                to="/login"
                className={`text-sm font-medium transition-colors ${
                  isTransparent 
                    ? "text-white/80 hover:text-white" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Log in
              </Link>
            )}
            <Button 
              asChild 
              className={`rounded-full px-6 transition-all ${
                isTransparent 
                  ? "bg-white text-primary hover:bg-white/90" 
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              }`}
            >
              <Link to={user ? "/onboarding" : "/register"}>
                Start a Tribute
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 transition-colors ${
              isTransparent ? "text-white" : "text-foreground"
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/50 bg-background">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {resourceLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-3 pt-4 border-t border-border/50">
                {user ? (
                  <Button asChild variant="ghost" className="justify-start">
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      Dashboard
                    </Link>
                  </Button>
                ) : (
                  <Link
                    to="/login"
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Log in
                  </Link>
                )}
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full">
                  <Link to={user ? "/onboarding" : "/register"} onClick={() => setMobileMenuOpen(false)}>
                    Start a Tribute
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
